import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { orderAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import NotificationBell from '../components/NotificationBell';
import { toast } from 'sonner';
import {
  Zap, Home, User, BarChart3, Users, CreditCard, Settings, LogOut, Menu,
  UserPlus, Calendar, ShoppingBag, ClipboardList, Package, ChevronDown, ChevronUp
} from 'lucide-react';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  processing: 'bg-blue-100 text-blue-700',
  shipped: 'bg-purple-100 text-purple-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

const UserOrdersPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await orderAPI.list();
        setOrders(res.data);
      } catch (error) { toast.error('Failed to load orders'); }
      finally { setLoading(false); }
    };
    fetchOrders();
  }, []);

  const handleLogout = async () => { await logout(); navigate('/'); };

  const navItems = [
    { icon: <Home className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
    { icon: <User className="w-5 h-5" />, label: 'Profiles', path: '/dashboard' },
    { icon: <CreditCard className="w-5 h-5" />, label: 'Artifacts', path: '/artifacts' },
    { icon: <Calendar className="w-5 h-5" />, label: 'Scheduling', path: '/scheduling' },
    { icon: <UserPlus className="w-5 h-5" />, label: 'Customers', path: '/customers' },
    { icon: <Users className="w-5 h-5" />, label: 'Leads', path: '/leads' },
    { icon: <BarChart3 className="w-5 h-5" />, label: 'Analytics', path: '/analytics' },
    { icon: <ShoppingBag className="w-5 h-5" />, label: 'Shop', path: '/dashboard/shop' },
    { icon: <ClipboardList className="w-5 h-5" />, label: 'Orders', path: '/dashboard/orders', active: true },
    { icon: <Settings className="w-5 h-5" />, label: 'Settings', path: '/settings' },
  ];
  if (user?.role === 'super_admin') {
    navItems.splice(9, 0, { icon: <Zap className="w-5 h-5" />, label: 'Admin', path: '/admin' });
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center"><Zap className="w-5 h-5 text-white" /></div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Signa</span>
          </Link>
        </div>
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item, i) => (
              <li key={i}>
                <Link to={item.path} className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${location.pathname === item.path ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-100'}`}>
                  {item.icon}<span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold text-sm">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={handleLogout}>
            <LogOut className="w-5 h-5 mr-3" />Sign out
          </Button>
        </div>
      </aside>

      <div className="dashboard-content">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 h-16">
            <button className="lg:hidden p-2 text-slate-600" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
            <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>My Orders</h1>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <Button onClick={() => navigate('/dashboard/shop')} className="bg-indigo-600 hover:bg-indigo-700" data-testid="shop-btn">
                <ShoppingBag className="w-4 h-4 mr-2" /> Shop
              </Button>
            </div>
          </div>
        </header>

        <main className="p-6" data-testid="user-orders-page">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Order History ({orders.length})
              </h3>
            </div>

            {orders.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No orders yet</p>
                <p className="text-sm text-slate-400 mb-4">Visit the shop to purchase your first artifact</p>
                <Button onClick={() => navigate('/dashboard/shop')} className="bg-indigo-600 hover:bg-indigo-700">
                  <ShoppingBag className="w-4 h-4 mr-2" /> Browse Shop
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {orders.map(order => (
                  <div key={order.order_id} data-testid={`order-${order.order_id}`}>
                    <button
                      onClick={() => setExpandedOrder(expandedOrder === order.order_id ? null : order.order_id)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                          <Package className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-slate-900 font-mono text-sm">{order.order_id}</p>
                          <p className="text-sm text-slate-500">{new Date(order.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-semibold text-slate-900">₱{order.total_price?.toLocaleString()}</span>
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize ${STATUS_COLORS[order.status] || 'bg-slate-100 text-slate-600'}`}>
                          {order.status}
                        </span>
                        {expandedOrder === order.order_id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </button>

                    {expandedOrder === order.order_id && (
                      <div className="px-6 pb-4 border-t border-slate-100 pt-3">
                        <div className="grid sm:grid-cols-2 gap-4 mb-3">
                          <div>
                            <p className="text-xs text-slate-500 font-medium mb-1">Shipping Address</p>
                            <p className="text-sm text-slate-900">{order.shipping_address}</p>
                          </div>
                          {order.notes && (
                            <div>
                              <p className="text-xs text-slate-500 font-medium mb-1">Notes</p>
                              <p className="text-sm text-slate-900">{order.notes}</p>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 font-medium mb-2">Items</p>
                        <div className="space-y-1">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-sm">
                              <div className="flex items-center gap-2">
                                <Package className="w-4 h-4 text-indigo-500" />
                                <span className="text-slate-900">{item.name}</span>
                                <span className="text-slate-400">x{item.quantity}</span>
                              </div>
                              <span className="font-medium text-slate-700">₱{(item.price * item.quantity).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default UserOrdersPage;
